/*
 * elome-core.js — global élome front-end foundation.
 *
 * Phase 2: Lenis smooth scroll (standalone — GSAP arrives in Phase 3).
 *
 *   • Fully gated behind prefers-reduced-motion: when reduced motion is
 *     requested we never construct Lenis, leaving the browser's native scroll.
 *   • Paused whenever Dawn scroll-locks <body> for an overlay (cart drawer,
 *     mobile menu, search modal) so Lenis never fights Dawn's own lock.
 */
(function () {
  'use strict';

  // Init is deferred to DOMContentLoaded so window.Lenis — loaded synchronously
  // (no defer) from the CDN <script> ahead of this deferred file — is guaranteed
  // present. Previously both scripts were deferred and Lenis could race past
  // the élome initializer, so `new window.Lenis()` never ran and window.elomeLenis stayed
  // undefined.
  function initLenis() {
  // 1 — Respect reduced-motion: bail before constructing Lenis (native scroll).
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  // 2 — Lenis ships from the CDN <script> immediately above this one in
  //     theme.liquid; bail gracefully if it failed to load.
  if (typeof window.Lenis === 'undefined') return;

  var lenis = new window.Lenis({
    duration: 1.2,
    easing: function (t) {
      return Math.min(1, 1.001 - Math.pow(2, -10 * t));
    },
    orientation: 'vertical',
    smoothWheel: true,
  });

  // Expose the instance for Phase 3 (GSAP ScrollTrigger will hook into lenis).
  window.elomeLenis = lenis;

  // 3 — Drive Lenis from the requestAnimationFrame loop.
  function raf(time) {
    lenis.raf(time);
    requestAnimationFrame(raf);
  }
  requestAnimationFrame(raf);

  // 4 — Never fight Dawn's scroll-lock. Dawn flags an open overlay by adding an
  //     `overflow-hidden*` class to <body>: the cart drawer and search modal add
  //     bare `overflow-hidden`; the MENU drawer adds `overflow-hidden-desktop`
  //     (its <header-drawer> is data-breakpoint="desktop"). Match any variant by
  //     substring so BOTH the cart drawer and the mobile menu pause Lenis.
  function syncScrollLock() {
    if (document.body.className.indexOf('overflow-hidden') !== -1) {
      lenis.stop();
    } else {
      lenis.start();
    }
  }

  var lockObserver = new MutationObserver(syncScrollLock);
  lockObserver.observe(document.body, {
    attributes: true,
    attributeFilter: ['class'],
  });

  // Sync once on load in case an overlay is already open.
  syncScrollLock();

  // Mobile homepage transparent header. On the homepage at mobile widths the
  // header starts transparent over the hero (`header--hero` on <body>) and turns
  // solid the instant the page scrolls. Driven by Lenis's scroll event, so it
  // lives here where the instance is created; reduced-motion already bailed
  // above, so the header simply stays solid then. Desktop + other templates are
  // untouched.
  if (window.innerWidth <= 749 && document.body.classList.contains('template-index')) {
    // Start transparent before any scroll event fires (avoids a solid flash).
    document.body.classList.add('header--hero');
    lenis.on('scroll', function (e) {
      var scroll = e && e.scroll;
      if (scroll === 0) {
        document.body.classList.add('header--hero');
      } else if (scroll > 0) {
        document.body.classList.remove('header--hero');
      }
    });
  }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initLenis);
  } else {
    initLenis();
  }
})();

/*
 * Transparent-header initial state and native-scroll fallback without Lenis.
 */
(function () {
  'use strict';

  function syncNativeHeroHeader() {
    var atTop = window.scrollY <= 0 && window.innerWidth <= 749;
    if (document.body.classList.contains('header--hero') !== atTop) {
      document.body.classList.toggle('header--hero', atTop);
    }
  }

  function initHeroHeader() {
    if (document.body.dataset.pageType !== 'index') return;
    syncNativeHeroHeader();
    if (!window.elomeLenis) {
      window.addEventListener('scroll', syncNativeHeroHeader, { passive: true });
      window.addEventListener('resize', syncNativeHeroHeader);
      window.addEventListener('pageshow', syncNativeHeroHeader);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initHeroHeader);
  } else {
    initHeroHeader();
  }
})();

/*
 * Phase 3: scroll-triggered text reveals (IntersectionObserver — no GSAP).
 *
 *   • Targets are marked `.elome-reveal` from JS (never Liquid) so content
 *     stays visible if JS is unavailable.
 *   • Each target fades + rises 20px once as it enters the viewport, then
 *     unobserves (fire-once).
 *   • Siblings in one section (e.g. the 3 Manifesto pillars) cascade by 80ms
 *     via an inline transition-delay, grouped by parent.
 *   • Reduced-motion: opacity-only fade (no translate, no stagger) — the
 *     translate/timing difference is handled by the CSS @media block.
 */
(function () {
  'use strict';

  // Legacy browsers without IO: leave content visible (never mark it hidden).
  if (!('IntersectionObserver' in window)) return;

  var SELECTOR = [
    // .banner__heading intentionally NOT here — the homepage hero scroll
    // sequence (Lenis IIFE below) fades the whole .banner__content (heading
    // included) per frame; a second IO reveal would double-animate it.
    '.elome-collection-hero .collection-hero__title',
    '.elome-manifesto__title',
    '.elome-manifesto__pillar',
    '.elome-manifesto__value',
    '.collection-list-title',
    '.collection__title',
    '.card__heading',
    '.collapsible-content__heading',
    '.elome-editorial__text',
    '.elome-editorial__media'
  ].join(',');

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var observer = null;

  function onIntersect(entries, obs) {
    for (var i = 0; i < entries.length; i++) {
      if (!entries[i].isIntersecting) continue;
      entries[i].target.classList.add('is-visible');
      obs.unobserve(entries[i].target); // reveal once
    }
  }

  // Idempotent: safe to re-run (e.g. on bfcache restore). Already-visible nodes
  // are left untouched so they never re-hide or re-animate.
  function setupReveal() {
    var targets = document.querySelectorAll(SELECTOR);
    if (!targets.length) return;

    // One observer; drop any prior one so a re-init doesn't stack observers.
    if (observer) observer.disconnect();
    observer = new IntersectionObserver(onIntersect, {
      threshold: 0.15,
      rootMargin: '0px 0px -50px 0px'
    });

    // Stagger siblings by 80ms (transition-delay), grouped by parent so
    // unrelated targets in other sections don't accumulate a delay.
    var indexByParent = new Map();

    targets.forEach(function (el) {
      // Already revealed (incl. a bfcache restore): leave as-is — never re-hide.
      if (el.classList.contains('is-visible')) return;

      // A node can't run two reveal systems at once: where Dawn's native
      // reveal (animations_reveal_on_scroll) sits on this SAME node, drop it
      // so only the élome reveal drives it.
      el.classList.remove('scroll-trigger', 'scroll-trigger--offscreen');

      el.classList.add('elome-reveal');

      if (!reduce) {
        var parent = el.parentNode;
        var n = indexByParent.get(parent) || 0;
        if (n > 0) el.style.transitionDelay = n * 80 + 'ms';
        indexByParent.set(parent, n + 1);
      }

      observer.observe(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupReveal);
  } else {
    setupReveal();
  }

  // Phase 4 — back/forward-cache restore. bfcache preserves the DOM (incl.
  // `.is-visible`), so re-running setupReveal() leaves revealed elements alone
  // and just re-attaches the observer to any still-pending ones. Plain loads
  // (persisted === false) already ran via the readyState path above.
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) setupReveal();
  });
})();

/*
 * Phase 4: page-transition crossfade (View Transitions API).
 *
 * The crossfade is CSS-driven for cross-document (MPA) navigations via
 * `@view-transition { navigation: auto }` in elome-transitions-product.css — there is nothing
 * for JS to drive, and unsupported browsers (Safari < 18, Firefox) ignore the
 * at-rule and navigate instantly (no shim). This block is only the progressive-
 * enhancement signal: flag the root element when the API is supported. It does
 * not touch Lenis or the reveal observers.
 */
(function () {
  'use strict';
  if (typeof document.startViewTransition === 'function') {
    document.documentElement.classList.add('elome-view-transitions');
  }
})();
